import React from "react";
import { useApi } from "../../../hooks/useApi";

/**
 * useClassicOverview — the one org-scope read behind the redesigned lede,
 * plus the DETERMINISTIC composition layer (headline, beats, attention).
 *
 * Composition is code, not a model: every sentence quotes a figure that is in
 * the payload, so the lede can never claim what the panels don't show.
 * (Same policy as drilldownApi.projectTakeaways.)
 */

const fmtDate = (d) => {
  const dt = new Date(d);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
};
// Same precise format as components/classic/micro.jsx `fmtH` — kept local so
// this hook stays importable without pulling in the component module.
const fmtH = (m) => {
  const v = Math.max(0, Math.round(Number(m) || 0));
  const h = Math.floor(v / 60);
  return h ? `${h}h ${String(v % 60).padStart(2, "0")}m` : `${v}m`;
};

/* ── the lede's story beats — each claim carries its proof and a target ──── */
function composeStory(d) {
  if (!d || !d.totals) return [];
  const beats = [];
  const total = d.totals.trackedMinutes || 0;

  const top = (d.projects || [])[0];
  if (top && total > 0) {
    const share = Math.round((top.minutes / total) * 100);
    beats.push({
      kind: "share",
      html: `<b>${escapeHtml(top.name)}</b> took <b>${share}%</b> of all tracked time`,
      segments: (d.projects || []).slice(0, 4).map((p, i) => ({
        pct: Math.max(2, Math.round((p.minutes / total) * 100)),
        hot: i === 0 && share >= 35,
      })),
      tip: `${top.name} · ${fmtH(top.minutes)} of ${fmtH(total)} — click to open the project`,
      drill: { kind: "project", id: top.projectId, name: top.name },
    });
  }

  const grower = (d.projects || [])
    .filter((p) => p.prevMinutes >= 60 && p.minutes > p.prevMinutes * 1.25)
    .sort((a, b) => (b.minutes / b.prevMinutes) - (a.minutes / a.prevMinutes))[0];
  if (grower) {
    const pct = Math.round(((grower.minutes - grower.prevMinutes) / grower.prevMinutes) * 100);
    beats.push({
      kind: "compare",
      html: `<b>${escapeHtml(grower.name)}</b> grew <b>+${pct}%</b> on the previous period`,
      rows: [
        { label: "Previous period", minutes: grower.prevMinutes, hot: false },
        { label: "This period", minutes: grower.minutes, hot: true },
      ],
      tip: `${fmtH(grower.prevMinutes)} → ${fmtH(grower.minutes)} — click to open the project`,
      drill: { kind: "project", id: grower.projectId, name: grower.name },
    });
  } else if (total > 0) {
    const idlePct = Math.round((d.totals.idleMinutes / total) * 100);
    beats.push({
      kind: "compare",
      html: `Idle ran at <b>${idlePct}%</b> of tracked time`,
      rows: [
        { label: "Active", minutes: d.totals.activeMinutes, hot: false },
        { label: "Idle", minutes: d.totals.idleMinutes, hot: idlePct >= 25 },
      ],
      tip: "Idle is time the tracker saw no input — not time off.",
      drill: null,
    });
  }

  const peaks = findPeaks(d.hourCurve || []);
  if (peaks) {
    beats.push({
      kind: "curve",
      html: peaks.second != null
        ? `The working day has <b>two peaks</b> — ${hh(peaks.first)} and a second at ${hh(peaks.second)}`
        : `The working day peaks at <b>${hh(peaks.first)}</b>`,
      curve: d.hourCurve,
      peaks,
      tip: "Active time by hour across the window",
      drill: null,
    });
  }
  return beats.slice(0, 3);
}

/** Two genuine peaks = two local maxima ≥3h apart with a ≥25% dip between. */
function findPeaks(curve) {
  if (!curve || !curve.some((v) => v > 0)) return null;
  const max = Math.max(...curve);
  const first = curve.indexOf(max);
  let second = null;
  for (let h = 0; h < 24; h += 1) {
    if (Math.abs(h - first) < 3) continue;
    if (curve[h] < max * 0.6) continue;
    const lo = Math.min(h, first); const hi = Math.max(h, first);
    const dip = Math.min(...curve.slice(lo, hi + 1));
    if (dip <= curve[h] * 0.75 && (second === null || curve[h] > curve[second])) second = h;
  }
  return { first, second };
}
const hh = (h) => `${String(h).padStart(2, "0")}:00`;
const escapeHtml = (s) => String(s || "").replace(/[<>&"]/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;", '"': "&quot;" }[c]));

/** One plain sentence over the same figures the beats prove. */
function composeHeadline(d, beats) {
  if (!d || !d.totals || !d.totals.trackedMinutes) return null;
  const t = d.totals;
  const projCount = (d.projects || []).length;
  const shareBeat = beats.find((b) => b.kind === "share");
  const lead = `The team put <b>${fmtH(t.trackedMinutes)}</b> into ${projCount || "its"} project${projCount === 1 ? "" : "s"}`;
  if (shareBeat) {
    const m = shareBeat.html.match(/<b>(\d+)%<\/b>/);
    const share = m ? m[1] : null;
    const name = (d.projects[0] && d.projects[0].name) || "";
    if (share && Number(share) >= 35) return `${lead} — but <em>${escapeHtml(name)}</em> alone took <em>${share}%</em> of it.`;
    if (share) return `${lead}, led by ${escapeHtml(name)} at ${share}%.`;
  }
  return `${lead}.`;
}

/* ── deterministic attention list — thresholds with a "so what" ──────────── */
function composeAttention(d) {
  if (!d || !d.totals) return [];
  const out = [];
  const t = d.totals;
  const total = t.trackedMinutes || 0;

  if ((d.people?.silent || []).length > 0) {
    const names = d.people.silent.slice(0, 3).map((s) => s.name.split(" ")[0]).join(", ");
    out.push({
      sev: "warning",
      title: `${d.people.silent.length} of ${d.people.total} tracked nothing this period`,
      detail: `${names}${d.people.silent.length > 3 ? " and others" : ""}. Either tracking is not running for them or they worked outside it — one message settles it.`,
      drill: d.people.silent[0] ? { kind: "person", id: d.people.silent[0].userId, name: d.people.silent[0].name } : null,
    });
  }

  const top = (d.projects || [])[0];
  if (top && total > 0 && top.minutes / total >= 0.35) {
    out.push({
      sev: "warning",
      title: `${top.name} absorbed ${Math.round((top.minutes / total) * 100)}% of the team`,
      detail: top.prevMinutes > 0
        ? `${fmtH(top.minutes)} against ${fmtH(top.prevMinutes)} last period.`
        : `${fmtH(top.minutes)} of ${fmtH(total)} tracked.`,
      drill: { kind: "project", id: top.projectId, name: top.name },
    });
  }

  const sw = d.detail?.switches;
  if (sw && sw.prevTotal > 0) {
    const delta = Math.round(((sw.total - sw.prevTotal) / sw.prevTotal) * 100);
    if (Math.abs(delta) >= 15) {
      out.push({
        sev: delta > 0 ? "warning" : "positive",
        title: `Tool switching ${delta > 0 ? "up" : "down"} ${Math.abs(delta)}%`,
        detail: `Average ${sw.avgGapMinutes || "—"} minutes between switches. Based on activity detail from ${d.detail.coverage.covered} of ${d.detail.coverage.total} people.`,
        drill: null,
      });
    }
  }

  if (total > 0 && t.prevTrackedMinutes > 0) {
    const delta = Math.round(((total - t.prevTrackedMinutes) / t.prevTrackedMinutes) * 100);
    const idlePct = Math.round((t.idleMinutes / total) * 100);
    if (delta >= 5 && idlePct <= 18) {
      out.push({
        sev: "positive",
        title: `Hours up ${delta}% while idle held at ${idlePct}%`,
        detail: "More work without more waste — the shape you want under load.",
        drill: null,
      });
    } else if (delta <= -20) {
      out.push({
        sev: "negative",
        title: `Tracked time fell ${Math.abs(delta)}% on the previous period`,
        detail: `${fmtH(total)} against ${fmtH(t.prevTrackedMinutes)}. Usually leave, a handover, or tracking that stopped — check which before reading anything into it.`,
        drill: null,
      });
    }
  }
  return out.slice(0, 4);
}

export function useClassicOverview(range, member) {
  const api = useApi();
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  // bumped by reload() so a failed read can be retried without a page refresh
  const [nonce, setNonce] = React.useState(0);
  // set when the SERVER says the window is empty and tells us why
  const [emptyReason, setEmptyReason] = React.useState(null);
  const reload = React.useCallback(() => setNonce((n) => n + 1), []);

  const startKey = range?.start ? fmtDate(range.start) : null;
  const endKey = range?.end ? fmtDate(range.end) : null;

  React.useEffect(() => {
    if (!startKey || !endKey) return undefined;
    let alive = true;
    setLoading(true); setError(null); setEmptyReason(null);
    // No timezone param: the server buckets days in the ORG timezone — the
    // one clock that matches how agent hours are stored. Sending the browser
    // tz made two viewers see different numbers for the same org.
    // Member dropdown scope — the server validates it against the viewer's
    // accessible set (403 outside), so this is a narrowing, never a widening.
    const memberQ = member ? `&userId=${encodeURIComponent(member)}` : "";
    api.get(`/api/v1/classic-dashboard/overview?startDate=${startKey}&endDate=${endKey}${memberQ}`)
      .then((res) => {
        if (!alive) return;
        const payload = res.data?.data || null;
        /* Never hand the { empty: true } shape downstream — nothing that
           consumes this hook expects a payload without totals. */
        if (payload && payload.empty) {
          setEmptyReason(payload.reason || "No data for this period");
          setData(null);
          return;
        }
        setData(payload);
      })
      .catch((e) => { if (alive) setError(e?.response?.data?.error?.message || e.message || "Could not load the overview."); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [api, startKey, endKey, member, nonce]);

  const story = React.useMemo(() => composeStory(data), [data]);
  const headline = React.useMemo(() => composeHeadline(data, story), [data, story]);
  const attention = React.useMemo(() => composeAttention(data), [data]);

  return { overview: data, story, headline, attention, loading, error, emptyReason, reload };
}

export default useClassicOverview;
